import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { FormData } from './types';

interface Props {
  formData: FormData;
  onChange: (field: keyof FormData, value: string | string[]) => void;
  onBack: () => void;
  onSubmit: () => Promise<void>;
  submitting: boolean;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '14px 16px',
  border: '1.5px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '18px',
  fontFamily: "'Zen Maru Gothic', sans-serif",
  outline: 'none',
  color: '#333',
  letterSpacing: '1px',
};

const backBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#bbb',
  fontSize: '12px',
  cursor: 'pointer',
  padding: '4px 8px',
  textDecoration: 'underline',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function StepPhone({ formData, onChange, onBack, onSubmit, submitting }: Props) {
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');

  const canSubmit =
    formData.phone.trim() !== '' &&
    formData.email.trim() !== '';

  const validateAndSubmit = () => {
    const normalized = formData.phone.replace(/[-\s]/g, '');
    if (!/^[0-9]{11}$/.test(normalized)) {
      setPhoneError('携帯電話番号を11桁で入力してください（例：09012345678）');
      return;
    }
    setPhoneError('');

    if (!EMAIL_RE.test(formData.email.trim())) {
      setEmailError('メールアドレスの形式をご確認ください（例：hanako@example.com）');
      return;
    }
    setEmailError('');

    onSubmit();
  };

  return (
    <div>
      <p style={{ fontWeight: 700, fontSize: '15px', marginBottom: '6px', color: '#333' }}>
        Q5. ご連絡先を入力してください
      </p>
      <p style={{ fontSize: '12px', color: '#aaa', marginBottom: '16px' }}>
        専任アドバイザーよりご連絡いたします
      </p>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '6px', fontWeight: 600 }}>
          電話番号
          <span style={{ fontSize: '11px', color: '#fa7c9e', marginLeft: '6px' }}>ハイフンなし・11桁</span>
        </label>
        <input
          name="phone"
          type="tel"
          style={inputStyle}
          placeholder="09012345678"
          value={formData.phone}
          inputMode="numeric"
          maxLength={13}
          onChange={(e) => {
            onChange('phone', e.target.value);
            setPhoneError('');
          }}
        />
        {phoneError && (
          <p style={{ color: '#dc3545', fontSize: '12px', marginTop: '6px' }}>{phoneError}</p>
        )}
      </div>

      {/* メールアドレス */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '6px', fontWeight: 600 }}>
          メールアドレス
        </label>
        <input
          name="email"
          type="email"
          style={{ ...inputStyle, fontSize: '16px', letterSpacing: 0 }}
          placeholder="hanako@example.com"
          value={formData.email}
          inputMode="email"
          autoComplete="email"
          onChange={(e) => {
            onChange('email', e.target.value);
            setEmailError('');
          }}
        />
        {emailError && (
          <p style={{ color: '#dc3545', fontSize: '12px', marginTop: '6px' }}>{emailError}</p>
        )}
        <p style={{ fontSize: '11px', color: '#aaa', marginTop: '4px' }}>
          お電話が繋がらない場合のご連絡と、求人情報のお送り先に利用します
        </p>
      </div>

      {/* LINE遷移の注意書き（送信ボタン直上に置いて、押す前に必ず目に入るようにする） */}
      <div
        style={{
          marginBottom: '16px',
          padding: '12px 14px',
          background: '#f3fbf4',
          border: '1.5px solid #06c755',
          borderRadius: '8px',
          fontSize: '12px',
          lineHeight: 1.8,
          color: '#333',
        }}
      >
        <p style={{ fontWeight: 700, marginBottom: '4px', color: '#06c755' }}>
          <i className="ri-line-fill" style={{ marginRight: '4px' }} />
          送信後、LINEの友だち追加画面に移動します
        </p>
        <p style={{ color: '#555' }}>
          求人のご案内はLINEでお送りします。友だち追加まで完了していただくと、
          担当者からのご連絡がスムーズです。
          <br />
          ※友だち追加をされない場合でも、お電話でのご連絡は可能です。
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
        <div style={{ textAlign: 'center', width: '100%' }}>
          <p style={{ fontSize: '11px', color: '#ff5c73', marginBottom: '6px', fontWeight: 700 }}>
            完全無料！
          </p>
          <button
            className="btn-next"
            style={{
              width: '100%',
              maxWidth: '320px',
              padding: '16px 24px',
              fontSize: '16px',
              opacity: submitting ? 0.7 : 1,
            }}
            onClick={validateAndSubmit}
            disabled={submitting || !canSubmit}
          >
            {submitting ? '送信中...' : '無料で求人を紹介してもらう'}
          </button>
        </div>
        <button style={backBtnStyle} onClick={onBack} disabled={submitting}>
          ← 前の質問に戻る
        </button>
      </div>
    </div>
  );
}